/**
 * Maps a Kusto `| render` directive (`QueryResultSet.renderHint`, extracted
 * from the response's QueryProperties frame by the kusto adapter — never
 * from query text, D7) to a `ChartConfig` the existing `ChartPanel` can
 * render immediately.
 *
 * Reuses `getCategoryFromDataType` (columnTypeDetection.ts) for numeric/date
 * column detection instead of re-matching dataType strings inline — it
 * already classifies every Kusto-mapped dataType (bigint/int/float/decimal
 * -> 'numeric', datetime -> 'date').
 */
import type { QueryColumn } from '../../../../types/queryExecution';
import type { ChartConfig, ChartType } from '../types/chartTypes';
import { getCategoryFromDataType } from './columnTypeDetection';

export type RenderHint = {
  visualization: string;
  title?: string;
  xcolumn?: string;
  ycolumns?: string[];
};

const VISUALIZATION_TO_CHART_TYPE: Record<string, ChartType> = {
  timechart: 'line',
  linechart: 'line',
  barchart: 'bar',
  columnchart: 'bar',
  piechart: 'pie',
  areachart: 'area',
  scatterchart: 'scatter',
};

/** Pure. Returns `null` when the visualization is unsupported or no numeric y-axis column exists. */
export function renderHintToChartConfig(
  hint: RenderHint,
  columns: QueryColumn[],
): ChartConfig | null {
  const type = VISUALIZATION_TO_CHART_TYPE[hint.visualization];
  if (!type) return null;

  // The hint names columns; the config must hold their ROW keys (`id ?? name`).
  const byName = new Map(columns.map((c) => [c.name, c]));
  const rowKey = (c: QueryColumn): string => c.id ?? c.name;
  const dateColumns = columns.filter((c) => getCategoryFromDataType(c.dataType) === 'date');
  const numericColumns = columns.filter((c) => getCategoryFromDataType(c.dataType) === 'numeric');

  const xColumn =
    (hint.xcolumn ? byName.get(hint.xcolumn) : undefined) ?? dateColumns[0] ?? columns[0];
  if (!xColumn) return null;
  const xColumnKey = rowKey(xColumn);

  const explicitYColumns = (hint.ycolumns ?? []).flatMap((name) => byName.get(name) ?? []);
  const yColumnKeys =
    explicitYColumns.length > 0
      ? explicitYColumns.map(rowKey)
      : numericColumns.map(rowKey).filter((key) => key !== xColumnKey);
  if (yColumnKeys.length === 0) return null;

  return {
    type,
    xAxis: { column: xColumnKey },
    yAxis: yColumnKeys.map((column) => ({ column, aggregation: 'none' })),
    showLegend: true,
    showGrid: true,
    ...(hint.title ? { title: hint.title } : {}),
  };
}
